/**
 * STG — Service order
 * - Open modal instead of submitting /cart/add (button text doesn't matter)
 * - Fill intro block: product title + price (via /variants/<id>.js)
 * - Auto-fill + visually-hide Shopify Forms "Variant ID" field (keeps it submittable)
 * - Fire Google Ads conversion ONLY on Shopify Forms SUCCESS state (NO text matching)
 */

(() => {
  const DEBUG = false;

  // Google Ads conversion send_to for "Confirm Installation Request"
  const ADS_SEND_TO = 'AW-17788461703/hg6PCOnU1v0bEIfFmaJC';

  const ADS_CURRENCY =
    window.Shopify?.currency?.active ||
    document.querySelector('meta[property="og:price:currency"]')?.content ||
    'USD';

  const log = (...a) => { if (DEBUG) console.log('[STG]', ...a); };

  // =========================
  // Modal
  // =========================
  const modal = document.querySelector('[data-stg-modal]');
  if (!modal) return;

  // =========================
  // Variant + title + price
  // =========================
  const getSelectedVariantId = () => {
    const form = document.querySelector('form[action*="/cart/add"]');
    const idInput = form?.querySelector('input[name="id"]');
    return idInput?.value || null;
  };

  const getTitleFromDom = () =>
    document.querySelector('[data-product-title]')?.textContent?.trim() ||
    document.querySelector('main h1')?.textContent?.trim() ||
    document.querySelector('h1')?.textContent?.trim() ||
    document.title;

  const formatMoney = (cents) => {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    if (window.Shopify?.formatMoney) {
      const fmt = window.Shopify?.money_format || window.Shopify?.currency?.money_format || '${{amount}}';
      try { return window.Shopify.formatMoney(n, fmt); } catch (_) {}
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: ADS_CURRENCY }).format(n / 100);
    } catch (_) {
      return String(n / 100);
    }
  };

  const variantCache = new Map();
  const fetchVariant = async (variantId) => {
    if (!variantId) return null;
    const key = String(variantId);
    if (variantCache.has(key)) return variantCache.get(key);
    const p = fetch(`/variants/${encodeURIComponent(key)}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(r => (r.ok ? r.json() : null)).catch(() => null);
    variantCache.set(key, p);
    return p;
  };

  let lastPriceValue = null; // numeric for Ads

  const resolvePriceText = async (variantId) => {
    const v = await fetchVariant(variantId);
    const cents = v?.price;
    if (typeof cents === 'number') {
      lastPriceValue = cents / 100;
      return formatMoney(cents);
    }
    lastPriceValue = null;
    return '';
  };

  // =========================
  // Intro UI
  // =========================
  const introProduct = modal.querySelector('[data-stg-intro-product]');
  const introPrice = modal.querySelector('[data-stg-intro-price]');

  const renderIntro = async () => {
    const variantId = getSelectedVariantId();
    const title = getTitleFromDom();

    if (introProduct) introProduct.textContent = title || '';
    if (introPrice) introPrice.textContent = '$—';

    if (variantId) {
      const pendingKey = String(variantId);
      if (introPrice) introPrice.dataset.stgPendingVariant = pendingKey;

      const priceText = await resolvePriceText(variantId);

      if (introPrice && introPrice.dataset.stgPendingVariant === pendingKey) {
        introPrice.textContent = priceText || '$—';
      }
    }
  };

  // =========================
  // Shopify Forms roots
  // =========================
  const getPortalRoot = () =>
    document.querySelector('#app-embed[data-form-root="true"]') ||
    document.querySelector('[data-form-root="true"]') ||
    document.querySelector('#app-embed') ||
    null;

  const getFormsEmbedHost = () => {
    const inside = modal.querySelector('shopify-forms-embed');
    if (inside) return inside;
    return document.querySelector('shopify-forms-embed');
  };

  const getCandidateRoots = () => {
    const roots = [];
    const host = getFormsEmbedHost();
    if (host?.shadowRoot) roots.push(host.shadowRoot);
    const portal = getPortalRoot();
    if (portal) roots.push(portal);
    if (roots.length === 0) roots.push(document.body);
    return roots;
  };

  const isVisible = (el) => {
    if (!el) return false;
    const s = window.getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || Number(s.opacity) === 0) return false;
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  };

  // =========================
  // Variant ID field: fill + visually hide (keep submittable)
  // =========================
  const findVariantFieldInRoot = (root) => {
    if (!root) return null;
    const input =
      root.querySelector('input[id="custom#variant_id"]') ||
      root.querySelector('input[data-testid="field-custom#variant_id"]');
    const label = root.querySelector('label[id="label-custom#variant_id"]');
    return input ? { input, label } : null;
  };

  const visuallyHide = (el) => {
    if (!el) return;
    el.style.position = 'absolute';
    el.style.left = '-9999px';
    el.style.width = '1px';
    el.style.height = '1px';
    el.style.opacity = '0';
    el.style.pointerEvents = 'none';
  };

  const fillAndHideVariantField = () => {
    const variantId = getSelectedVariantId();
    if (!variantId) return false;

    const roots = getCandidateRoots();
    let found = null;
    for (const r of roots) {
      found = findVariantFieldInRoot(r);
      if (found) break;
    }
    if (!found) return false;

    const { input, label } = found;

    input.value = String(variantId);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const row = input.closest('div') || input.parentElement;
    if (row) {
      row.style.margin = '0';
      row.style.padding = '0';
      row.style.border = '0';
      row.style.height = '0';
      row.style.minHeight = '0';
      row.style.overflow = 'hidden';
      row.style.opacity = '0';
      row.style.pointerEvents = 'none';
      row.style.position = 'relative';
    }

    visuallyHide(input);
    if (label) label.style.display = 'none';

    return true;
  };

  const tryFillAndHideVariantField = () => {
    let tries = 0;
    const maxTries = 120;
    const timer = setInterval(() => {
      tries += 1;
      const ok = fillAndHideVariantField();
      if (ok || tries >= maxTries) clearInterval(timer);
    }, 100);
  };

  // =========================
  // Conversion firing
  // =========================
  let adsArmed = false; // armed ONLY after submit attempt (or safe submit-button click fallback)
  let adsFired = false;

  const fireAdsConversion = () => {
    if (adsFired) return;
    adsFired = true;

    const payload = { send_to: ADS_SEND_TO };
    if (typeof lastPriceValue === 'number' && Number.isFinite(lastPriceValue)) {
      payload.value = lastPriceValue;
      payload.currency = ADS_CURRENCY;
    }

    log('FIRE CONVERSION', payload);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'stg_installation_request_success', ...payload });
      console.warn('STG: gtag not found. Install Google tag (gtag.js) on the site.');
    }
  };

  // ✅ Robust success detection (based on your DOM)
  const detectSuccess = () => {
    const portal = getPortalRoot();

    // 1) Strongest: data-current-step="success" on portal root
    if (portal && portal.getAttribute('data-current-step') === 'success') {
      log('Success: portal data-current-step=success');
      return true;
    }

    // 2) Strong: banner-success testid
    if (portal) {
      const banner = portal.querySelector('section[data-testid="banner-success"], [data-testid="banner-success"]');
      if (banner && isVisible(banner)) {
        log('Success: data-testid=banner-success');
        return true;
      }
    }

    // 3) Strong: any data-current-step="success" under roots
    for (const r of getCandidateRoots()) {
      const step = r.querySelector?.('[data-current-step="success"]');
      if (step && isVisible(step)) {
        log('Success: nested data-current-step=success');
        return true;
      }

      // 4) Generic success testids
      const generic =
        r.querySelector?.('[data-testid*="success" i]') ||
        r.querySelector?.('[data-testid*="submitted" i]') ||
        r.querySelector?.('[data-testid*="confirmation" i]') ||
        r.querySelector?.('[data-testid*="complete" i]');
      if (generic && isVisible(generic)) {
        log('Success: generic success testid');
        return true;
      }
    }

    return false;
  };

  let successObs = null;
  let successPoll = null;

  const stopSuccessWatcher = () => {
    if (successObs) {
      successObs.disconnect();
      successObs = null;
    }
    if (successPoll) {
      clearInterval(successPoll);
      successPoll = null;
    }
  };

  const startSuccessWatcher = () => {
    stopSuccessWatcher();

    const roots = getCandidateRoots();

    const check = () => {
      if (!adsArmed || adsFired) return;
      if (detectSuccess()) {
        stopSuccessWatcher();
        fireAdsConversion();
      }
    };

    successObs = new MutationObserver(() => requestAnimationFrame(check));
    for (const r of roots) {
      try {
        successObs.observe(r, { childList: true, subtree: true, characterData: true });
      } catch (_) {}
    }

    successPoll = setInterval(check, 250);
    setTimeout(() => stopSuccessWatcher(), 60_000);

    check();
  };

  // =========================
  // Arm on submit (and safe click fallback by testid)
  // =========================
  const armAdsOnFormSubmit = () => {
    const tryBind = () => {
      const roots = getCandidateRoots();

      for (const r of roots) {
        const form = r.querySelector?.('form[data-testid="form"], form');
        if (form && !form.__stgAdsBound) {
          form.__stgAdsBound = true;

          // Primary: real submit
          form.addEventListener('submit', () => {
            adsArmed = true;
            log('Forms submit caught → armed');
            startSuccessWatcher();
          }, true);

          // Safe fallback: only the real submit button (if Forms blocks submit in JS)
          r.addEventListener('click', (e) => {
            const btn = e.target.closest('button, input[type="submit"]');
            if (!btn) return;
            const tid = (btn.getAttribute('data-testid') || '').toLowerCase();
            if (!tid.includes('btn-form-submit')) return;

            adsArmed = true;
            log('Submit button click caught → armed');
            startSuccessWatcher();
          }, true);

          return true;
        }
      }

      return false;
    };

    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (tryBind() || tries >= 120) clearInterval(t);
    }, 100);
  };

  // =========================
  // Modal open/close
  // =========================
  const openModal = () => {
    modal.hidden = false;

    adsArmed = false;
    adsFired = false;

    renderIntro();
    tryFillAndHideVariantField();
    armAdsOnFormSubmit();
  };

  const closeModal = () => {
    modal.hidden = true;
    stopSuccessWatcher();
    adsArmed = false;
  };

  modal.addEventListener('click', (e) => {
    if (e.target.closest('[data-stg-close]')) closeModal();
    if (e.target.classList.contains('stgModal__bg')) closeModal();
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !modal.hidden) closeModal();
  });

  // =========================
  // Intercept PDP submit (/cart/add) -> open modal
  // =========================
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;

    const action = (form.getAttribute('action') || '').toLowerCase();
    if (!action.includes('/cart/add')) return;

    e.preventDefault();
    e.stopImmediatePropagation();
    openModal();
  }, true);

  // Variant change while modal open
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t) return;
    if (t.name === 'id' || t.closest?.('variant-selects') || t.closest?.('variant-radios')) {
      if (!modal.hidden) {
        setTimeout(() => {
          renderIntro();
          tryFillAndHideVariantField();
        }, 80);
      }
    }
  });
})();